'use client';
import { useCallback, useEffect, useState } from 'react';
import { Check, Plus, Trash2 } from 'lucide-react';
import { adminFetch, getAdminToken } from '../../lib/admin';

/**
 * Referrals TAB（P1 夥伴計劃版）：三個子區
 * ① 夥伴 Partners（審核制列表＋佣金三段）② 佣金 Deals（狀態機工作台＋標記成交）
 * ③ 推薦碼 Codes（既有 /gf 登記功能保留）
 * API：/partners /deals /ref-settings /gf-codes（寫入走 x-admin-token）
 */

const CF_WORKER_URL = 'https://jkd-whatsapp-verification.jimsbond007.workers.dev';

interface Partner {
  name: string; phone: string; code: string; status: string;
  createdAt?: number; clicks: number; leads: number; verified: number;
  wonAmount12m: number; commPending: number; commApproved: number; commPaid: number;
}
interface Deal {
  id: string; leadToken: string; leadName?: string; partnerCode: string;
  amount: number; rate: number; commission: number; currency: string;
  partnerName?: string; partnerPhone?: string; clientPhone?: string; clientEmail?: string;
  status: string; note?: string; createdAt?: number;
}
interface GfCode { code: string; target: string; campaign: string; clicks: number; leads: number; conversion: number; }
interface Rules { percentage: number; tierThreshold: number; tierRate: number; currency: string; }

type Sub = 'partners' | 'deals' | 'codes';

export default function ReferralsTab() {
  const [sub, setSub] = useState<Sub>('partners');
  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        {([['partners', '夥伴 Partners'], ['deals', '佣金 Deals'], ['codes', '推薦碼 Codes']] as [Sub, string][]).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSub(id)}
            className={`px-4 py-1.5 rounded text-sm font-bold transition-colors ${
              sub === id ? 'bg-jkd-gold text-jkd-black' : 'bg-jkd-gray-100/40 text-gray-300 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {sub === 'partners' && <PartnersSection />}
      {sub === 'deals' && <DealsSection />}
      {sub === 'codes' && <CodesSection />}
    </div>
  );
}

/* ============ ① 夥伴 ============ */
function PartnersSection() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<Rules | null>(null);
  const [rulesForm, setRulesForm] = useState<Rules | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${CF_WORKER_URL}/partners`).then(r => r.json()),
      fetch(`${CF_WORKER_URL}/ref-settings`).then(r => r.json()),
    ]).then(([p, s]) => {
      setPartners(p.partners || []);
      setRules(s.rules || null);
      setRulesForm(s.rules || null);
    }).catch(() => setPartners([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setStatus = async (code: string, action: 'approve' | 'suspend' | 'reactivate') => {
    const res = await adminFetch(`/partners/${encodeURIComponent(code)}/${action}`, { method: 'POST' });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success) { setMsg(`✅ ${code} → ${d.status}`); fetchAll(); }
    else setMsg(`❌ ${d.error || res.status}`);
  };

  const saveRules = async () => {
    if (!rulesForm) return;
    const res = await adminFetch('/ref-settings', { method: 'POST', body: JSON.stringify(rulesForm) });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success) { setMsg('✅ 規則已更新'); setRules(d.rules); }
    else setMsg(`❌ ${d.error || res.status}`);
  };

  const exportPartnersXlsx = async () => {
    const XLSX = await import('xlsx');
    const rows = partners.map(p => ({
      Name: p.name, Code: p.code, Phone: p.phone, Status: p.status,
      Clicks: p.clicks, Leads: p.leads, Verified: p.verified,
      Won12m: p.wonAmount12m, CommPending: p.commPending, CommApproved: p.commApproved, CommPaid: p.commPaid,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Partners');
    XLSX.writeFile(wb, `jkd-partners-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const statusBadge = (st: string) => {
    const cls = st === 'active' ? 'bg-green-500/20 text-green-400'
      : st === 'pending' ? 'bg-yellow-500/20 text-yellow-400'
      : 'bg-red-500/20 text-red-400';
    return <span className={`px-2.5 py-0.5 rounded text-xs font-bold ${cls}`}>{st}</span>;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-bold text-white">夥伴列表（審核制）</h2>
        <div className="flex items-center gap-3">
          {msg && <span className="text-xs text-gray-300">{msg}</span>}
          <button onClick={exportPartnersXlsx} className="text-sm text-gray-400 hover:text-jkd-gold inline-flex items-center gap-1.5">
            匯出 XLSX
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">載入中…</p>
      ) : partners.length === 0 ? (
        <p className="text-sm text-gray-500">尚無夥伴 — 招募頁 https://jkdcoding.com/partner 上線後，申請會出現在這裡待審核。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs font-mono uppercase tracking-wider border-b border-jkd-gray-200">
                <th className="py-2 pr-3">夥伴</th><th className="py-2 pr-3">狀態</th>
                <th className="py-2 pr-3">點擊</th><th className="py-2 pr-3">Lead</th><th className="py-2 pr-3">已驗證</th>
                <th className="py-2 pr-3">成交12m</th><th className="py-2 pr-3">佣金待批</th><th className="py-2 pr-3">已審核</th><th className="py-2 pr-3">已付</th>
                <th className="py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.code} className="border-b border-jkd-gray-200/50 last:border-0">
                  <td className="py-2 pr-3">
                    <div className="text-white font-bold">{p.name}</div>
                    <a href={`https://jkdcoding.com/gf/${p.code}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-jkd-gold/80 hover:text-jkd-gold">/gf/{p.code}</a>
                  </td>
                  <td className="py-2 pr-3">{statusBadge(p.status)}</td>
                  <td className="py-2 pr-3 text-gray-300">{p.clicks}</td>
                  <td className="py-2 pr-3 text-gray-300">{p.leads}</td>
                  <td className="py-2 pr-3 text-gray-300">{p.verified}</td>
                  <td className="py-2 pr-4 text-white font-bold">HK${(p.wonAmount12m || 0).toLocaleString()}</td>
                  <td className="py-2 pr-3 text-yellow-400">{p.commPending || 0}</td>
                  <td className="py-2 pr-3 text-blue-400">{p.commApproved || 0}</td>
                  <td className="py-2 pr-3 text-green-400">{p.commPaid || 0}</td>
                  <td className="py-2">
                    {p.status === 'pending' && (
                      <button onClick={() => setStatus(p.code, 'approve')} className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 text-xs font-bold mr-2">
                        <Check className="w-3.5 h-3.5" /> 審核通過
                      </button>
                    )}
                    {p.status === 'active' && (
                      <button onClick={() => setStatus(p.code, 'suspend')} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-xs font-bold">
                        <Trash2 className="w-3.5 h-3.5" /> 停用
                      </button>
                    )}
                    {p.status === 'suspended' && (
                      <button onClick={() => setStatus(p.code, 'reactivate')} className="text-xs text-gray-300 hover:text-white">重新啟用</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 佣金規則設定 */}
      <div className="card-dark">
        <h3 className="text-md font-bold text-white mb-3">佣金規則</h3>
        {rulesForm && (
          <div className="flex flex-wrap gap-4 items-end">
            <label className="text-xs text-gray-400">
              <span className="block mb-1">預設佣金 %</span>
              <input type="number" value={rulesForm.percentage} onChange={e => setRulesForm(f => f && { ...f, percentage: Number(e.target.value) })}
                className="w-24 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
            </label>
            <label className="text-xs text-gray-400">
              <span className="block mb-1">分層門檻（HK$）</span>
              <input type="number" value={rulesForm.tierThreshold} onChange={e => setRulesForm(f => f && { ...f, tierThreshold: Number(e.target.value) })}
                className="w-32 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
            </label>
            <label className="text-xs text-gray-400">
              <span className="block mb-1">分層佣金 %</span>
              <input type="number" value={rulesForm.tierRate} onChange={e => setRulesForm(f => f && { ...f, tierRate: Number(e.target.value) })}
                className="w-24 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
            </label>
            <button onClick={saveRules} className="btn-gold text-sm px-5 py-2">儲存規則</button>
            <span className="text-xs text-gray-600">規則：實收金額錨點；每筆成交可個別 override 佣金率；月結。</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ ② 佣金工作台 ============ */
function DealsSection() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [form, setForm] = useState({ leadToken: '', amount: '', rateOverride: '', note: '' });

  const fetchDeals = useCallback(() => {
    setLoading(true);
    fetch(`${CF_WORKER_URL}/deals`)
      .then(r => r.json())
      .then(d => setDeals(d.deals || []))
      .catch(() => setDeals([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const create = async () => {
    if (!form.leadToken.trim() || !form.amount) { setMsg('請填 Lead Token 與實收金額'); return; }
    const body: Record<string, unknown> = {
      leadToken: form.leadToken.trim(),
      amount: Number(form.amount),
      note: form.note,
    };
    if (form.rateOverride) body.rateOverride = Number(form.rateOverride);
    const res = await adminFetch('/deals', { method: 'POST', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success) {
      setMsg(`✅ 成交已登記 ${d.deal.id} — 佣金 HK$${d.deal.commission}（${d.deal.rate}%，pending）`);
      setForm({ leadToken: '', amount: '', rateOverride: '', note: '' });
      fetchDeals();
    } else setMsg(`❌ ${d.error || res.status}`);
  };

  const transition = async (id: string, action: 'approve' | 'paid' | 'cancel') => {
    let body: string | undefined;
    if (action === 'cancel') {
      const note = window.prompt('取消原因（必填，會記錄在 deal）：');
      if (!note) return;
      body = JSON.stringify({ note });
    }
    const res = await adminFetch(`/deals/${encodeURIComponent(id)}/${action}`, { method: 'POST', body });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success) fetchDeals();
    else window.alert(`${action} 失敗：${d.error || res.status}`);
  };

  const exportDealsXlsx = async () => {
    const XLSX = await import('xlsx');
    const rows = deals.map(d => ({
      DealID: d.id, Lead: d.leadName || d.leadToken, Partner: d.partnerCode,
      PartnerPhone: d.partnerPhone || '', ClientPhone: d.clientPhone || '', ClientEmail: d.clientEmail || '',
      Amount: d.amount, Rate: `${d.rate}%`, Commission: d.commission,
      Status: d.status, Note: d.note || '',
      Created: d.createdAt ? new Date(d.createdAt).toLocaleString('zh-HK') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Deals');
    XLSX.writeFile(wb, `jkd-deals-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const statusBadge = (st: string) => {
    const cls = st === 'paid' ? 'bg-green-500/20 text-green-400'
      : st === 'approved' ? 'bg-blue-500/20 text-blue-400'
      : st === 'pending' ? 'bg-yellow-500/20 text-yellow-400'
      : 'bg-red-500/20 text-red-400';
    return <span className={`px-2.5 py-0.5 rounded text-xs font-bold ${cls}`}>{st}</span>;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-bold text-white">佣金工作台（月結 SOP：pending → 審核 approved → 已付 paid）</h2>
        <div className="flex items-center gap-3">
          {msg && <span className="text-xs text-gray-300">{msg}</span>}
          <button onClick={exportDealsXlsx} className="text-sm text-gray-400 hover:text-jkd-gold">匯出 XLSX</button>
        </div>
      </div>

      {/* 標記成交 */}
      <div className="card-dark">
        <h3 className="text-md font-bold text-white mb-3">標記成交（實收金額錨點）</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-xs text-gray-400">
            <span className="block mb-1">Lead Token</span>
            <input value={form.leadToken} onChange={e => setForm(f => ({ ...f, leadToken: e.target.value }))}
              placeholder="從 Leads 列表複製"
              className="w-64 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm font-mono focus:border-jkd-gold focus:outline-none" />
          </label>
          <label className="text-xs text-gray-400">
            <span className="block mb-1">實收金額 HK$</span>
            <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              className="w-36 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
          </label>
          <label className="text-xs text-gray-400">
            <span className="block mb-1">佣金率 override %（選填）</span>
            <input type="number" value={form.rateOverride} onChange={e => setForm(f => ({ ...f, rateOverride: e.target.value }))}
              placeholder="留空=按規則"
              className="w-40 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
          </label>
          <label className="text-xs text-gray-400">
            <span className="block mb-1">備註</span>
            <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              className="w-48 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
          </label>
          <button onClick={create} className="btn-gold text-sm px-5 py-2 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> 標記成交
          </button>
        </div>
      </div>

      {/* Deals 表 */}
      {loading ? (
        <p className="text-sm text-gray-500">載入中…</p>
      ) : deals.length === 0 ? (
        <p className="text-sm text-gray-500">尚無成交紀錄 — 在上方登記第一筆。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs font-mono uppercase tracking-wider border-b border-jkd-gray-200">
                <th className="py-2 pr-3">Deal</th><th className="py-2 pr-3">客戶（電話）</th><th className="py-2 pr-3">夥伴（電話）</th>
                <th className="py-2 pr-3">實收</th><th className="py-2 pr-3">率</th><th className="py-2 pr-3">佣金</th>
                <th className="py-2 pr-3">狀態</th><th className="py-2 pr-3">備註</th><th className="py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {deals.map(d => (
                <tr key={d.id} className="border-b border-jkd-gray-200/50 last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs text-gray-400">{d.id}</td>
                  <td className="py-2 pr-3 text-gray-200">
                    {d.leadName || <span className="text-gray-500 font-mono text-xs">{d.leadToken}</span>}
                    {d.clientPhone && <div className="font-mono text-xs text-gray-500">{d.clientPhone}</div>}
                  </td>
                  <td className="py-2 pr-3 font-mono text-jkd-gold">/gf/{d.partnerCode}
                    {d.partnerPhone && <div className="text-xs text-gray-500">{d.partnerPhone}</div>}
                  </td>
                  <td className="py-2 pr-3 text-white font-bold">HK${d.amount.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-gray-400">{d.rate}%</td>
                  <td className="py-2 pr-3 text-jkd-gold font-bold">HK${d.commission.toLocaleString()}</td>
                  <td className="py-2 pr-3">{statusBadge(d.status)}</td>
                  <td className="py-2 pr-3 text-gray-500 text-xs max-w-[180px] truncate" title={d.note}>{d.note || '—'}</td>
                  <td className="py-2">
                    <div className="flex gap-2">
                      {d.status === 'pending' && (
                        <button onClick={() => transition(d.id, 'approve')} className="text-xs text-blue-400 hover:text-blue-300 font-bold">審核</button>
                      )}
                      {d.status === 'approved' && (
                        <button onClick={() => transition(d.id, 'paid')} className="text-xs text-green-400 hover:text-green-300 font-bold">已付佣</button>
                      )}
                      {(d.status === 'pending' || d.status === 'approved') && (
                        <button onClick={() => transition(d.id, 'cancel')} className="text-xs text-red-400 hover:text-red-300 font-bold">取消</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============ ③ 推薦碼（既有功能保留） ============ */
function CodesSection() {
  const [codes, setCodes] = useState<GfCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', target: '/diagnosis', campaign: '' });
  const [msg, setMsg] = useState<string | null>(null);

  const fetchCodes = () => {
    setLoading(true);
    fetch(`${CF_WORKER_URL}/gf-codes`)
      .then(res => res.json())
      .then(d => setCodes(d.codes || []))
      .catch(() => setCodes([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCodes(); }, []);

  const create = async () => {
    if (!form.code.trim()) { setMsg('請輸入短碼'); return; }
    const res = await adminFetch('/gf-codes', {
      method: 'POST',
      body: JSON.stringify({ code: form.code.trim(), target: form.target, campaign: form.campaign.trim() || 'gf' }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success) {
      setMsg(`✅ 已登記 /gf/${form.code.trim()} — https://jkdcoding.com/gf/${form.code.trim()}`);
      setForm({ code: '', target: '/diagnosis', campaign: '' });
      fetchCodes();
    } else setMsg(`❌ ${d.error || `HTTP ${res.status}`}`);
  };

  const remove = async (code: string) => {
    if (!window.confirm(`刪除推薦碼 /gf/${code}？`)) return;
    const res = await adminFetch(`/gf-codes/${encodeURIComponent(code)}`, { method: 'DELETE' });
    if (res.ok) fetchCodes();
  };

  return (
    <div className="card-dark">
      <h2 className="text-lg font-bold text-white mb-1">/gf 推薦碼（行銷短碼）</h2>
      <p className="text-xs text-gray-500 mb-4">
        帖文/私訊用短碼連結；夥伴的專屬碼會在建檔時自動出現（含 partner 標記），這裡登記的是行銷用短碼。
      </p>
      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <label className="text-xs text-gray-400">
          <span className="block mb-1">短碼</span>
          <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="例如 fb-post01"
            className="w-40 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
        </label>
        <label className="text-xs text-gray-400">
          <span className="block mb-1">目標頁</span>
          <select value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))}
            className="px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none">
            <option value="/diagnosis">/diagnosis（診斷）</option>
            <option value="/">/（首頁）</option>
            <option value="/landlink">/landlink</option>
            <option value="/bizpulse">/bizpulse</option>
            <option value="/promo">/promo</option>
          </select>
        </label>
        <label className="text-xs text-gray-400">
          <span className="block mb-1">Campaign</span>
          <input value={form.campaign} onChange={e => setForm(f => ({ ...f, campaign: e.target.value }))} placeholder="例如 launch"
            className="w-36 px-3 py-2 bg-jkd-black border border-jkd-gray-200 rounded text-white text-sm focus:border-jkd-gold focus:outline-none" />
        </label>
        <button onClick={create} className="btn-gold text-sm px-5 py-2 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> 登記
        </button>
        {msg && <span className="text-xs text-gray-300">{msg}</span>}
      </div>
      {loading ? (
        <p className="text-sm text-gray-500">載入中…</p>
      ) : codes.length === 0 ? (
        <p className="text-sm text-gray-500">尚無推薦碼。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs font-mono uppercase tracking-wider border-b border-jkd-gray-200">
                <th className="py-2 pr-4">短碼</th><th className="py-2 pr-4">目標</th><th className="py-2 pr-4">Campaign</th>
                <th className="py-2 pr-4">點擊</th><th className="py-2 pr-4">歸因 Lead</th><th className="py-2 pr-4">轉化率</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.code} className="border-b border-jkd-gray-200/50 last:border-0">
                  <td className="py-2 pr-4 font-mono text-jkd-gold">/gf/{c.code}</td>
                  <td className="py-2 pr-4 text-gray-300 font-mono text-xs">{c.target}</td>
                  <td className="py-2 pr-4 text-gray-400">{c.campaign}</td>
                  <td className="py-2 pr-4 text-white font-bold">{c.clicks}</td>
                  <td className="py-2 pr-4 text-gray-300">{c.leads}</td>
                  <td className="py-2 pr-4 text-gray-400">{c.clicks > 0 ? `${c.conversion}%` : '—'}</td>
                  <td className="py-2">
                    <button onClick={() => remove(c.code)} className="text-gray-500 hover:text-red-400 transition-colors" aria-label={`刪除 ${c.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!getAdminToken() && (
        <p className="mt-3 text-[11px] text-gray-600">提示：尚未設定 Admin Token — 第一次執行登記/刪除時會詢問。</p>
      )}
    </div>
  );
}
